import { FormControl, FormLabel, Grid, Stack } from '@mui/joy';
import React, { Dispatch, SetStateAction } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormReset,
  UseFormSetValue,
} from 'react-hook-form';

import { InputType } from 'core/enums';
import { enterPress } from 'core/utils';
import { Size } from 'theme/enums';

import { FieldGroupWrapper, FieldWrapper } from './style';

interface IProps {
  child;
  handleSubmit?: UseFormHandleSubmit<FieldValues>;
  isSubmitting?: boolean;
  onSubmit?: (
    reset: UseFormReset<FieldValues>,
    setError: Dispatch<SetStateAction<string>>
  ) => (data: FieldValues) => void;
  control?: Control<FieldValues, object>;
  setValue?: UseFormSetValue<FieldValues>;
  getValues?: UseFormGetValues<FieldValues>;
  errors: FieldErrors<FieldValues>;
  reset?: UseFormReset<FieldValues>;
  setError?: Dispatch<SetStateAction<string>>;
}

const getFieldError = (errors, child) => {
  const { inputProps, props } = child;

  if (inputProps?.error) {
    return inputProps.error;
  }

  const { name } = props;

  if (!name) {
    return null;
  }

  const splittedName = name?.split('[');
  if (splittedName.length > 1) {
    const [firstPart, secondPart] = splittedName;

    return errors?.[firstPart]?.[secondPart.replace(']', '')];
  }

  return errors?.[name];
};

const getDefaultSubmitOnEnter = (type) => {
  switch (type) {
    case InputType.TEXT_INPUT:
    case InputType.TEXTAREA:
    case InputType.NUMBER_INPUT:
    case InputType.DATE_INPUT:
      return true;
    default:
      return false;
  }
};

const FieldChildView = ({
  child,
  handleSubmit,
  isSubmitting = false,
  onSubmit,
  control,
  setValue,
  getValues,
  errors,
  reset,
  setError,
}: IProps) => {
  const getFieldInput = (child) =>
    React.createElement(child.type, {
      ...{
        ...child.props,
        inputProps: {
          ...child.props.inputProps,
          control,
          setValue,
          getValues,
          error: getFieldError(errors, child),
          onKeyDown: (e) => {
            if (child?.props?.inputProps?.onKeyDown) {
              child.props.inputProps.onKeyDown(e);
            }

            const submitOnEnter =
              child?.props?.inputProps?.submitOnEnter ||
              getDefaultSubmitOnEnter(child?.props?.type);

            // A mouse submit leaves the focus in the field, so Enter must not start a second one.
            if (onSubmit && submitOnEnter && !isSubmitting) {
              enterPress(e, handleSubmit(onSubmit(reset, setError)));
            }
          },
        },
        key: child.props.name,
      },
    });

  if (child?.props?.children) {
    const inline = child?.props?.inline === undefined ? true : child?.props?.inline;

    return (
      <Grid xs={12} md={6}>
        <Stack>
          {child?.props?.label && (
            <FormControl size={child?.props?.size || Size.SM}>
              <FormLabel required={child?.props?.required || false}>{child.props.label}</FormLabel>
            </FormControl>
          )}

          <FieldGroupWrapper inline={inline} sx={child?.props?.sx || {}}>
            {React.Children.map(child?.props?.children, (insideChild) => (
              <FieldWrapper
                sx={{ width: inline ? '48%' : '100%', ...(child?.props?.childSx || {}) }}
              >
                {getFieldInput(insideChild)}
              </FieldWrapper>
            ))}
          </FieldGroupWrapper>
        </Stack>
      </Grid>
    );
  }

  return getFieldInput(child);
};

export default FieldChildView;
